/**
 * /app/$tenantSlug/people/$id — employee detail view (CC2 2026-06-05).
 *
 * Fetches the employee record by ID from the HR-only directory
 * (GET /api/v1/employees). If the ID is not found the component throws,
 * which causes TanStack Router's `defaultErrorComponent` (RouteError) to
 * render — showing "Something went wrong loading this page", a "Try again"
 * button, and a "Back to dashboard" link. This is the surface tested by
 * e2e/cross-cutting-flows/cc2-error-boundary-recovery.spec.ts.
 *
 * HR admins see the full record; non-HR users see their own record if they
 * navigate to their own ID (via the directory), otherwise a 403 from the
 * backend causes useEmployees to error and we throw so the boundary fires.
 *
 * URL: /app/{slug}/people/{id}  (parent of /{id}/edit)
 */

import { createFileRoute, Link, Outlet, useRouterState, useParams } from "@tanstack/react-router";
import { ArrowLeft, User } from "lucide-react";
import { useAuth } from "@/lib/auth";
import type { Role } from "@/types/auth";
import { useEmployees } from "@/hooks/use-hr";
import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/shared/status-badge";

const CAN_VIEW_EMPLOYEES = new Set<Role>(["company_admin", "hr_manager"]);

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/people/$id",
)({
  component: PeopleDetailRoute,
});

function PeopleDetailRoute() {
  const { tenantSlug } = useParams({ strict: false }) as {
    tenantSlug: string;
  };
  const { id } = Route.useParams();
  const numId = Number(id);
  const validId = Number.isFinite(numId) && numId > 0;
  const { user } = useAuth();
  const allowed = !!user?.role && CAN_VIEW_EMPLOYEES.has(user.role);
  // When a child route (e.g. /edit) is active, delegate rendering to it
  // via <Outlet /> instead of showing the detail card. This allows
  // people.$id.edit.tsx to render its full EditEmployeeForm without
  // being overlaid by the detail view.
  const routerState = useRouterState();
  const hasActiveChild = routerState.location.pathname.endsWith("/edit");

  const q = useEmployees(allowed && validId);

  // Once data is loaded, find the employee — throw if missing so the
  // defaultErrorComponent (RouteError) catches it.
  const employee = q.data?.employees.find((e) => e.id === numId);

  // Throw on hard fetch error so the error boundary fires.
  if (q.isError) {
    throw new Error(
      q.error instanceof Error
        ? q.error.message
        : "Could not load employee record.",
    );
  }

  // Throw when data arrived but ID doesn't exist in this tenant.
  if (q.data && !employee) {
    throw new Error(`Employee #${numId} was not found in your company.`);
  }

  if (!validId) {
    throw new Error("The URL doesn't contain a valid employee ID.");
  }

  // Child route active (e.g. /edit) — render its content via Outlet.
  if (hasActiveChild) {
    return <Outlet />;
  }

  return (
    <>
      <PageHeader
        title={
          employee
            ? `${employee.first_name} ${employee.last_name}`
            : "Employee"
        }
        description={employee?.designation ?? undefined}
      />

      <div className="mb-4">
        <Button asChild variant="outline" className="gap-1.5">
          <Link to="/app/$tenantSlug/people" params={{ tenantSlug }}>
            <ArrowLeft className="h-4 w-4" />
            Back to People
          </Link>
        </Button>
      </div>

      {q.isLoading && (
        <Card className="max-w-2xl">
          <CardContent className="space-y-3 pt-6">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-2/3" />
          </CardContent>
        </Card>
      )}

      {employee && (
        <Card className="max-w-2xl">
          <CardHeader className="flex flex-row items-center gap-3 pb-4">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <User aria-hidden className="h-6 w-6" />
            </span>
            <div>
              <CardTitle className="text-lg">
                {employee.first_name} {employee.last_name}
              </CardTitle>
              {employee.designation && (
                <p className="text-sm text-muted-foreground">
                  {employee.designation}
                </p>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div>
                <dt className="font-medium text-muted-foreground">
                  Employee code
                </dt>
                <dd className="mt-0.5">{employee.employee_id ?? "—"}</dd>
              </div>
              <div>
                <dt className="font-medium text-muted-foreground">
                  Department
                </dt>
                <dd className="mt-0.5">{employee.department ?? "—"}</dd>
              </div>
              <div>
                <dt className="font-medium text-muted-foreground">Email</dt>
                <dd className="mt-0.5">{employee.email}</dd>
              </div>
              <div>
                <dt className="font-medium text-muted-foreground">Status</dt>
                <dd className="mt-0.5">
                  <StatusBadge
                    status={employee.is_active ? "active" : "inactive"}
                  />
                </dd>
              </div>
            </dl>

            {allowed && (
              <div className="pt-2">
                <Button asChild variant="default" size="sm">
                  <Link
                    to="/app/$tenantSlug/people/$id/edit"
                    params={{ tenantSlug, id }}
                  >
                    Edit employee
                  </Link>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
}
